import { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';

import ConfirmationDialog from '../../../ConfirmationDialog';

import * as clientSlice from '../../../../data/client.slice';
import { ClientLocation } from '../../../../data/models/client-location.model';
import { Client } from '../../../../data/models/client.model';

import ClientLocationList from './ClientLocationList';
import ClientLocationDialog from './ClientLocationDialog';
import { useIsSuperReaderUser } from '../../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ClientLocations';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
}));

interface ClientLocationsProps {
  client: Client;
}

export default function ClientLocations({ client }: ClientLocationsProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const dispatch = useAppDispatch();
  const locations = useAppSelector(clientSlice.selectClientLocations);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<ClientLocation>();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(clientSlice.fetchClientLocationsAsync(client.clientBC));
  }, [dispatch]);

  useEffect(() => {
    const namedLocationUUIDs = locations.map((l) => l.namedLocationUUID);
    if (namedLocationUUIDs.length) {
      dispatch(clientSlice.fetchNamedLocationsByUUIDsAsync(namedLocationUUIDs));
    }
  }, [dispatch, locations]);

  const handleCreate = () => setIsDialogOpen(true);

  const handleEdit = (location: ClientLocation) => {
    setSelectedLocation(location);
    setIsDialogOpen(true);
  };

  const handleDelete = (location: ClientLocation) => {
    setSelectedLocation(location);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    dispatch(
      clientSlice.deleteClientLocationAsync({
        clientBC: client.clientBC,
        clientLocationUUID: selectedLocation!.clientLocationUUID!,
        namedLocationUUID: selectedLocation!.namedLocationUUID!,
      }),
    );
    setSelectedLocation(undefined);
    setConfirmDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setSelectedLocation(undefined);
    setConfirmDialogOpen(false);
  };

  const handleClose = () => {
    setSelectedLocation(undefined);
    setIsDialogOpen(false);
  };

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {t('client.locations')}
        </Typography>
        {!isSuperReaderUser && (
          <Button variant="contained" color="primary" onClick={handleCreate}>
            {t('client.createLocation')}
          </Button>
        )}
      </Box>
      <Box>
        <ClientLocationList
          locations={locations}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Box>
      <ClientLocationDialog
        clientBC={client.clientBC}
        location={selectedLocation}
        open={isDialogOpen}
        onClose={handleClose}
      />

      <ConfirmationDialog
        title={t('client.confirmDeleteLocationTitle')}
        message={t('client.confirmDeleteLocationMessage')}
        open={confirmDialogOpen}
        positiveAction={handleConfirmDelete}
        negativeAction={handleCancelDelete}
      />
    </StyledBox>
  );
}
